import { useCallback, type FC } from 'react';
import { vegaArtifactGraph } from '@gonfalon/dogfood-flags/dogfood-flags';
import { MarkdownRenderer } from '@gonfalon/observability-ui/components/MarkdownRenderer';
import type {
  RenderArtifactTokenArgs,
  MarkdownRendererProps,
} from '@gonfalon/observability-ui/components/MarkdownRenderer';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';

import { isAllowedAgentContentUrl } from '../../../../utils/safeUrl';

import { ArtifactChipLazy } from './ArtifactChipLazy';

interface Props extends Pick<MarkdownRendererProps, 'onExternalSourceClick' | 'onTaskAction'> {
  text: string;
  projectKey: string;
}

// Vega-side wrapper that wires the artifact-graph flag and the lazy-fetch chip into
// the shared MarkdownRenderer. Use this anywhere a Vega assistant message is rendered
// so :::artifact{} tokens become chips when the flag is on.
export const VegaMarkdownRenderer: FC<Props> = ({ text, projectKey, onExternalSourceClick, onTaskAction }) => {
  const { projectId } = useProjectId();
  const artifactsEnabled = vegaArtifactGraph();

  const renderArtifactToken = useCallback(
    (args: RenderArtifactTokenArgs) => (
      <ArtifactChipLazy
        projectKey={args.projectKey}
        projectId={projectId}
        type={args.type}
        externalId={args.externalId}
      />
    ),
    [projectId],
  );

  return (
    <MarkdownRenderer
      text={text}
      projectKey={projectKey}
      onExternalSourceClick={onExternalSourceClick}
      artifactsEnabled={artifactsEnabled}
      onTaskAction={onTaskAction}
      renderArtifactToken={artifactsEnabled ? renderArtifactToken : undefined}
      isExternalUrlAllowed={isAllowedAgentContentUrl}
    />
  );
};
